import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { moduleMaterialsAPI } from '../../services/api';
import { translateLocation } from '../../utils/translationUtils';
import './MyCourses.css';

const MyCourses = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getStudentEnrollments, getCourseById, getStudentGrades, getStudentHomeworkSubmissions } = useData();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);

  const getEntityId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value.id || value._id || null;
    }
    return value;
  };

  const isSameId = (left, right) => {
    const a = getEntityId(left);
    const b = getEntityId(right);
    return a !== null && b !== null && String(a) === String(b);
  };

  // Get student's enrolled courses
  const userId = user?.id || user?._id;
  const enrollments = userId ? (getStudentEnrollments(userId) || []) : [];
  const grades = userId ? (getStudentGrades(userId) || []) : [];
  const homeworkSubmissions = userId ? (getStudentHomeworkSubmissions(userId) || []) : [];

  const getCourseDetails = (courseId) => {
    return getCourseById(courseId);
  };

  const getModuleDetails = (course) => {
    return course?.module;
  };

  const getStudyYearDetails = (course) => {
    return course?.studyYear;
  };

  const getStudyDetails = (course) => {
    return course?.study;
  };

  const getCourseGrades = (courseId) => {
    return grades.filter((g) => isSameId(g.courseId, courseId));
  };

  const getCourseHomework = (courseId) => {
    const course = getCourseById(courseId);
    if (!course?.module) return [];
    return course.module.homework || [];
  };

  const getCourseExams = (courseId) => {
    const course = getCourseById(courseId);
    if (!course?.module) return [];
    return course.module.exams || [];
  };

  const calculateProgress = (courseId) => {
    const courseGrades = getCourseGrades(courseId);
    const courseHomework = getCourseHomework(courseId);
    
    if (courseGrades.length === 0 && courseHomework.length === 0) return 0;
    
    const totalItems = courseGrades.length + courseHomework.length;
    const completedItems = [
      ...courseGrades,
      ...homeworkSubmissions.filter((submission) =>
        submission.homeworkId && courseHomework.find((hw) => isSameId(hw.id || hw._id, submission.homeworkId))
      )
    ].filter((item) => item.status === 'graded' || item.status === 'passed').length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  useEffect(() => {
    const loadMaterials = async () => {
      if (!selectedCourse) {
        setCourseMaterials([]);
        return;
      }

      const moduleIds = [
        ...(selectedCourse.module ? [selectedCourse.module.id || selectedCourse.module._id || selectedCourse.module] : []),
        ...((selectedCourse.modules || []).map((mod) => mod.id || mod._id || mod))
      ].filter(Boolean);

      if (moduleIds.length === 0) {
        setCourseMaterials([]);
        return;
      }

      setMaterialsLoading(true);
      try {
        const response = await moduleMaterialsAPI.getAll({ moduleIds: moduleIds.join(',') });
        setCourseMaterials(response.materials || []);
      } catch (error) {
        setCourseMaterials([]);
      } finally {
        setMaterialsLoading(false);
      }
    };

    loadMaterials();
  }, [selectedCourse]);

  const renderCourseDetail = (course) => {
    if (!course) return null;
    
    const module = getModuleDetails(course);
    const studyYear = getStudyYearDetails(course);
    const study = getStudyDetails(course);
    const courseId = getEntityId(course);
    const courseGrades = getCourseGrades(courseId);
    const courseHomework = getCourseHomework(courseId);
    const courseExams = getCourseExams(courseId);
    const progress = calculateProgress(courseId);

    return (
      <div className="course-detail-view">
        <button className="back-button" onClick={() => setSelectedCourse(null)}>
          ← {t('common.back')}
        </button>

        <div className="detail-header">
          <h2>{course.name}</h2>
          <span className="course-code-badge">{course.code}</span>
        </div>

        <div className="detail-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('portal.dashboard')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'grades' ? 'active' : ''}`}
            onClick={() => setActiveTab('grades')}
          >
            {t('nav.grades')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'homework' ? 'active' : ''}`}
            onClick={() => setActiveTab('homework')}
          >
            {t('nav.homework')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'exams' ? 'active' : ''}`}
            onClick={() => setActiveTab('exams')}
          >
            {t('nav.exams')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
            onClick={() => setActiveTab('materials')}
          >
            {t('nav.materials')}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="progress-section">
                <h3>{t('progress.title')}</h3>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${progress}%` }}>
                    {progress}%
                  </div>
                </div>
              </div>

              <div className="course-hierarchy">
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('study.title')}:</span>
                  <span className="hierarchy-value">{study?.name}</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('studyYear.title')}:</span>
                  <span className="hierarchy-value">{studyYear?.year}</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('module.title')}:</span>
                  <span className="hierarchy-value">{module?.name} ({module?.code})</span>
                </div>
                <div className="hierarchy-item">
                  <span className="hierarchy-label">{t('module.ects')}:</span>
                  <span className="hierarchy-value">{module?.ects}</span>
                </div>
              </div>

              <div className="course-info-grid">
                <div className="info-card">
                  <h4>{t('course.type')}</h4>
                  <p>{t(`courseType.${course.courseType || 'instructor-led'}`)}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.duration')}</h4>
                  <p>{course.duration} {t('course.days')}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.location')}</h4>
                  <p>{translateLocation(t, course.location)}</p>
                </div>
                <div className="info-card">
                  <h4>{t('course.startDate')}</h4>
                  <p>{course.startDate}</p>
                </div>
              </div>

              {course.courseType === 'instructor-led' && course.teamsUrl && (
                <div className="teams-section">
                  <h4>{t('course.teamsUrl')}</h4>
                  <a href={course.teamsUrl} target="_blank" rel="noopener noreferrer" className="teams-link">
                    📹 {t('exam.attend')} Teams Vergadering
                  </a>
                </div>
              )}

              {course.courseType === 'self-paced' && course.deadline && (
                <div className="deadline-section">
                  <h4>{t('course.deadline')}</h4>
                  <p className="deadline-date">{course.deadline}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'grades' && (
            <div className="grades-tab">
              <h3>{t('grade.title')}</h3>
              {courseGrades.length > 0 ? (
                <div className="grades-list">
                  {courseGrades.map((grade, index) => (
                    <div key={index} className="grade-item">
                      <span className="grade-label">{t('grade.score')}:</span>
                      <span className={`grade-value ${grade.score >= 55 ? 'passed' : 'failed'}`}>
                        {grade.score}%
                      </span>
                      <span className={`grade-status ${grade.status}`}>
                        {t(`grade.${grade.status}`)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
              
              <div className="passing-threshold">
                <p>{t('grade.passingThreshold')}</p>
              </div>
            </div>
          )}

          {activeTab === 'homework' && (
            <div className="homework-tab">
              <h3>{t('nav.homework')}</h3>
              {courseHomework.length > 0 ? (
                <div className="homework-list">
                  {courseHomework.map((hw) => {
                    const submission = homeworkSubmissions.find((s) => isSameId(s.homeworkId, hw.id || hw._id));
                    return (
                      <div key={hw.id} className="homework-item">
                        <div className="homework-header">
                          <h4>{hw.title}</h4>
                          <span className={`homework-status ${submission?.status || 'pending'}`}>
                            {submission?.status === 'submitted' ? t('homework.submitted') : t('common.pending')}
                          </span>
                        </div>
                        <p className="homework-description">{hw.description}</p>
                        <div className="homework-meta">
                          <span>{t('homework.dueDate')}: {hw.dueDate}</span>
                          <span>{t('homework.maxScore')}: {hw.maxScore}</span>
                        </div>
                        {submission && (
                          <div className="submission-result">
                            <span>{t('homework.grade')}: {submission.grade}</span>
                            {submission.feedback && (
                              <p className="feedback">{t('homework.feedback')}: {submission.feedback}</p>
                            )}
                          </div>
                        )}
                        {!submission && (
                          <button className="submit-homework-btn">
                            {t('homework.submit')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}

          {activeTab === 'exams' && (
            <div className="exams-tab">
              <h3>{t('nav.exams')}</h3>
              {courseExams.length > 0 ? (
                <div className="exams-list">
                  {courseExams.map((exam) => {
                    const grade = courseGrades.find((g) => isSameId(g.examId, exam.id || exam._id));
                    return (
                      <div key={exam.id} className="exam-item">
                        <div className="exam-header">
                          <h4>{exam.title}</h4>
                          <span className="exam-type">{t(`exam.${exam.type}`)}</span>
                        </div>
                        <div className="exam-details">
                          <span>{t('exam.date')}: {exam.date}</span>
                          <span>{t('exam.duration')}: {exam.duration} min</span>
                          <span>{t('exam.location')}: {translateLocation(t, exam.location)}</span>
                          {exam.room && <span>{t('exam.room')}: {exam.room}</span>}
                        </div>
                        <div className="exam-passing">
                          <span>{t('exam.passingScore')}: {exam.passingScore}%</span>
                        </div>
                        {grade && (
                          <div className="exam-grade">
                            <span className={`grade ${grade.score >= 55 ? 'passed' : 'failed'}`}>
                              {grade.score}% - {t(`grade.${grade.status}`)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="materials-tab">
              <h3>{t('nav.materials')}</h3>
              {materialsLoading && <p>{t('common.loading')}</p>}
              {!materialsLoading && courseMaterials.length > 0 ? (
                <div className="materials-list">
                  {courseMaterials.map((material) => (
                    <div key={material.id} className="material-item material-item-rich">
                      <span className="material-icon">
                        {material.type === 'media' ? '🎬' : material.type === 'document' ? '📄' : material.type === 'article' ? '📝' : '🔗'}
                      </span>
                      <div className="material-body">
                        <p className="material-link">{material.title}</p>
                        {material.description && <p className="material-description">{material.description}</p>}
                        {material.type === 'article' && material.articleContent && (
                          <div className="material-article" dangerouslySetInnerHTML={{ __html: material.articleContent }} />
                        )}
                        <div className="material-links">
                          {material.externalUrl && (
                            <a href={material.externalUrl} target="_blank" rel="noopener noreferrer" className="material-link-action">
                              Open link
                            </a>
                          )}
                          {material.file?.filename && (
                            <>
                              <a href={moduleMaterialsAPI.viewFileUrl(material.id)} target="_blank" rel="noopener noreferrer" className="material-link-action">
                                Bekijken
                              </a>
                              <a href={moduleMaterialsAPI.downloadFileUrl(material.id)} target="_blank" rel="noopener noreferrer" className="material-link-action">
                                Downloaden
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="material-type">{material.type}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('common.noData')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (selectedCourse) {
    return (
      <div className="my-courses-page">
        {renderCourseDetail(selectedCourse)}
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      <div className="page-header">
        <h1>{t('portal.myCourses')}</h1>
      </div>

      <div className="courses-grid">
        {enrollments.map((enrollment) => {
          const enrollmentCourseId = getEntityId(enrollment.courseId);
          const course = getCourseDetails(enrollmentCourseId);
          if (!course) return null;

          const module = getModuleDetails(course);
          const studyYear = getStudyYearDetails(course);
          const progress = calculateProgress(enrollmentCourseId);
          
          return (
            <div 
              key={enrollment.id || enrollment._id || enrollmentCourseId} 
              className="course-detail-card"
              onClick={() => setSelectedCourse(course)}
            >
              <div className="course-badge">
                {enrollment.status === 'active' ? t('enrollment.active') : enrollment.status}
              </div>
              
              <h3 className="course-title">{course?.name}</h3>
              <p className="course-code">{course?.code}</p>
              
              <div className="course-info">
                <div className="info-row">
                  <span className="info-label">{t('studyYear.title')}:</span>
                  <span className="info-value">{studyYear?.year}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('module.title')}:</span>
                  <span className="info-value">{module?.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('course.duration')}:</span>
                  <span className="info-value">{course?.duration} {t('course.days')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('course.location')}:</span>
                  <span className="info-value">{translateLocation(t, course?.location)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('module.ects')}:</span>
                  <span className="info-value">{module?.ects}</span>
                </div>
              </div>

              <div className="course-progress">
                <div className="progress-label">
                  <span>{t('progress.title')}</span>
                  <span>{progress}%</span>
                </div>
                <div className="progress-bar-small">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              </div>

              <div className="course-type-badge">
                {course?.courseType === 'self-paced' ? '📚 Zelfstudie' : '👨‍🏫 Docentgeleid'}
              </div>
            </div>
          );
        })}
        
        {enrollments.length === 0 && (
          <div className="no-courses">
            <p>{t('portal.noCourses')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
